import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { TD_SESSION_ENTRY_KEY } from '../SessionEntryCapture'
import EmailSender from '../../api/emailsender'

const STORAGE_DISMISSED = 'td_newsletter_modal_dismissed'
const STORAGE_AUTO_SHOWN = 'td_nl_modal_auto_shown_session'
const OPEN_DELAY_MS = 4200

function normalizePathname(path) {
    if (!path) return '/'
    const base = path.split('?')[0] || '/'
    if (base === '/' || base === '') return '/'
    const trimmed = base.replace(/\/+$/, '')
    return trimmed === '' ? '/' : trimmed
}

/** Home-only: once per tab session when first load was home (or unknown yet), and user did not leave home in SPA before open. */
function shouldScheduleHomeNewsletterModal() {
    if (typeof window === 'undefined') return false
    try {
        if (sessionStorage.getItem(STORAGE_DISMISSED)) return false
        if (sessionStorage.getItem(STORAGE_AUTO_SHOWN)) return false
        if (sessionStorage.getItem('td_user_left_home_in_spa')) return false

        const current = normalizePathname(window.location.pathname)
        if (current !== '/') return false

        const stored = sessionStorage.getItem(TD_SESSION_ENTRY_KEY)
        if (stored == null) {
            return true
        }
        return normalizePathname(stored) === '/'
    } catch {
        try {
            return normalizePathname(window.location.pathname) === '/'
        } catch {
            return false
        }
    }
}

function NewsletterLeadModal() {
    const [mounted, setMounted] = useState(false)
    const [open, setOpen] = useState(false)
    const [submitted, setSubmitted] = useState(false)
    const { isSubmitting, error, sendEmail, setError } = EmailSender()

    useEffect(() => {
        setMounted(true)
    }, [])

    useLayoutEffect(() => {
        if (typeof window === 'undefined') return
        try {
            if (!sessionStorage.getItem(TD_SESSION_ENTRY_KEY)) {
                sessionStorage.setItem(TD_SESSION_ENTRY_KEY, normalizePathname(window.location.pathname))
            }
        } catch {
            /* ignore */
        }
    }, [])

    useEffect(() => {
        if (typeof window === 'undefined') return undefined

        let openTimer
        const defer = window.setTimeout(() => {
            if (!shouldScheduleHomeNewsletterModal()) return
            openTimer = window.setTimeout(() => {
                try {
                    sessionStorage.setItem(STORAGE_AUTO_SHOWN, '1')
                } catch {
                    /* ignore */
                }
                setOpen(true)
            }, OPEN_DELAY_MS)
        }, 0)

        return () => {
            window.clearTimeout(defer)
            if (openTimer) window.clearTimeout(openTimer)
        }
    }, [])

    useEffect(() => {
        if (!open) return undefined
        const prev = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return () => {
            document.body.style.overflow = prev
        }
    }, [open])

    const close = useCallback(() => {
        setOpen(false)
        try {
            sessionStorage.setItem(STORAGE_DISMISSED, '1')
        } catch {
            /* ignore */
        }
    }, [])

    useEffect(() => {
        if (!open) return undefined
        const onKey = (e) => {
            if (e.key === 'Escape') close()
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [open, close])

    const onSubmit = async (e) => {
        e.preventDefault()
        setError(null)
        const form = e.currentTarget
        const formData = {
            Name: form.name.value.trim(),
            Phone: form.phone.value.trim(),
            Email: form.email.value.trim(),
        }
        const ok = await sendEmail(formData, 'TechDigit — Home page lead (modal)')
        if (ok) {
            setSubmitted(true)
            window.setTimeout(() => {
                close()
            }, 1600)
        }
    }

    if (!mounted) return null

    const modal = (
        <AnimatePresence>
            {open && (
                <motion.div
                    key="td-newsletter-modal"
                    className="td-nl-modal-root"
                    role="presentation"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
                >
                    <button
                        type="button"
                        className="td-nl-modal__backdrop"
                        aria-label="Close dialog"
                        onClick={close}
                    />
                    <div className="td-nl-modal__center" role="dialog" aria-modal="true" aria-labelledby="td-nl-modal-title">
                        <motion.div
                            className="td-nl-modal__shell"
                            initial={{ opacity: 0, scale: 0.92, y: 36 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            transition={{
                                type: 'spring',
                                damping: 27,
                                stiffness: 340,
                                mass: 0.82,
                            }}
                        >
                            <button type="button" className="td-nl-modal__close" onClick={close} aria-label="Close">
                                <span aria-hidden="true">×</span>
                            </button>
                            <div className="td-nl-modal__grid">
                                <div className="td-nl-modal__form-col">
                                    <div className="td-nl-modal__brand">
                                        <img src={`${process.env.PUBLIC_URL}/assets/images/logo/techdigit.png`} alt="TechDigit Solutions" />
                                    </div>
                                    <h2 id="td-nl-modal-title" className="td-nl-modal__title">
                                        Get in touch
                                    </h2>
                                    {submitted ? (
                                        <motion.p
                                            className="td-nl-modal__thanks"
                                            initial={{ opacity: 0, y: 8 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.35 }}
                                        >
                                            Thanks—we&apos;ll be in touch.
                                        </motion.p>
                                    ) : (
                                        <form className="td-nl-modal__form" onSubmit={onSubmit}>
                                            {error && (
                                                <p className="text-danger small mb-2" role="alert">
                                                    {error}
                                                </p>
                                            )}
                                            <label className="td-nl-modal__label td-nl-modal__label--field">
                                                <span className="td-nl-modal__sr-only">Name</span>
                                                <input
                                                    name="name"
                                                    type="text"
                                                    autoComplete="name"
                                                    placeholder="Name"
                                                    aria-label="Name"
                                                    required
                                                />
                                            </label>
                                            <label className="td-nl-modal__label td-nl-modal__label--field">
                                                <span className="td-nl-modal__sr-only">Phone</span>
                                                <input
                                                    name="phone"
                                                    type="tel"
                                                    autoComplete="tel"
                                                    placeholder="Phone"
                                                    aria-label="Phone"
                                                    required
                                                />
                                            </label>
                                            <label className="td-nl-modal__label td-nl-modal__label--field">
                                                <span className="td-nl-modal__sr-only">Email</span>
                                                <input
                                                    name="email"
                                                    type="email"
                                                    autoComplete="email"
                                                    placeholder="Email"
                                                    aria-label="Email"
                                                    required
                                                />
                                            </label>
                                            <button type="submit" className="td-nl-modal__submit" disabled={isSubmitting}>
                                                {isSubmitting ? 'Sending…' : 'Submit'}
                                            </button>
                                        </form>
                                    )}
                                </div>
                                <div
                                    className="td-nl-modal__visual"
                                    style={{
                                        backgroundImage: `url(${process.env.PUBLIC_URL}/assets/images/newsletter-modal-bg.jpg)`,
                                    }}
                                >
                                    <div className="td-nl-modal__visual-overlay" aria-hidden="true" />
                                    <div className="td-nl-modal__visual-copy">
                                        <p className="td-nl-modal__visual-kicker">TechDigit Solutions</p>
                                        <p className="td-nl-modal__visual-headline">Built for teams who run tight operations</p>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    )

    return createPortal(modal, document.body)
}

export default NewsletterLeadModal
